import { FC, useMemo } from "react";
import { useDispatch } from "react-redux";

import { useEventContext } from "Bands/contexts";
import { editEvent } from "Bands/state";
import { EventBlocksType, IEvent } from "Bands/types";
import { copy } from "shared/assets";
import { FormBuilder, Message, Modal } from "shared/components";
import { Spacer } from "shared/layout";
import { IFieldType, IFormFields, IFormOption, IStatus } from "shared/types";

interface Props {
  submittedEventData: Partial<IEvent>;
  setShowChangedDateModal: (value: Partial<IEvent> | undefined) => void;
  blockTypesToUpdateOptions: IFormOption[];
  hasRSVPs: boolean;
}

interface IUpdateEventDateBlockTypesFormProps {
  resendRSVP: boolean;
  blockTypesToUpdate: EventBlocksType[];
}

export const ChangedDateModal: FC<Props> = ({
  submittedEventData,
  setShowChangedDateModal,
  blockTypesToUpdateOptions,
  hasRSVPs,
}) => {
  const { event } = useEventContext();
  const dispatch = useDispatch();

  const formFields: IFormFields = useMemo(() => {
    let fields: IFormFields = [];

    if (!!blockTypesToUpdateOptions.length) {
      fields.push({
        name: "blockTypesToUpdate",
        label: "Please adapt the following dates accordingly:",
        type: IFieldType.checkboxGroup,
        options: blockTypesToUpdateOptions,
        validation: {
          required: copy.errors.required,
        },
        style: {
          gridColumn: "span 4",
        },
      });
    }

    if (hasRSVPs) {
      hasRSVPs &&
        fields.push({
          name: "resendRSVP",
          label: "Resend existing RSVP request for the new date",
          superLabel: (
            <Message status={IStatus.info}>
              You have sent RSVP requests for this event. Do you want us to
              resend a new request for the new date?
              <br />
              The current requests and answers will be invalidated.
            </Message>
          ),
          type: IFieldType.checkbox,
        });
    }

    return fields;
  }, [blockTypesToUpdateOptions, hasRSVPs]);

  const handleDateChangedSubmit = ({
    blockTypesToUpdate,
    resendRSVP,
  }: IUpdateEventDateBlockTypesFormProps) => {
    event &&
      dispatch(
        editEvent({
          data: submittedEventData,
          id: event.id,
          blockTypesToUpdate,
          resendRSVP: !!resendRSVP,
        })
      );
  };

  return (
    <Modal showCloseButton>
      <Spacer size="xs">
        {!!blockTypesToUpdateOptions.length && (
          <Message status={IStatus.info}>
            Since you have changed the date for this event, you also might want
            to adapt the dates in the event's schedule, travel and accommodation
            blocks as well. We can assist you with that.
          </Message>
        )}

        <FormBuilder
          fields={formFields}
          onSubmit={handleDateChangedSubmit}
          submitMode="isDirty"
          buttons={{
            primary: {
              children: "Adapt dates",
            },
            secondary: {
              children: "No thanks",
              onClick: () => {
                setShowChangedDateModal(undefined);
                event &&
                  dispatch(
                    editEvent({ data: submittedEventData, id: event.id })
                  );
              },
            },
          }}
        />
      </Spacer>
    </Modal>
  );
};
